/**
 * 神奈川県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「ゆで落花生」は千葉に、「奈良茶飯」は奈良に、「豆腐の田楽」は愛知の味噌田楽と重なるので外した。
 * - 「よこすか海軍カレー」は広島の呉海自カレーと題材が重なるので外した。
 * - 「ワカサギの甘露煮」は栃木の「あいその甘露煮」と絵が重なるので外した。
 * - 「けんちん汁」は茨城にも伝わるが、神奈川側は建長寺発祥という由来、700年以上前からという年数、
 *   初代住職が修行僧の落とした豆腐を拾い集めて汁に入れたという逸話、ごま油で炒めてから煮ること、
 *   この4点が公的な出典から取れるので、鎌倉の品として入れた。
 * - 「小田原かまぼこ」は宮城の笹かまぼこ、鳥取のちくわと違い「板からぷっくりとはみ出した扇形」。
 * - シューマイの消費額は「冷凍食品や飲食店での食事は除く年間平均消費額」という限定つきの数字なので、
 *   そのまま書いている。「消費日本一」と縮めない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：石垣団子・かんこ焼き・へらへら団子・酒まんじゅう・のらぼう菜のおひたし・けんちん汁・マグロのかぶと焼き（店で食べられる品に差し替え）
 * - 小田原おでん：おでん祭りの開催日・回数は書かない（毎年変わる）
 * - 厚木シロコロ・ホルモン：「シロコロ」という呼び名は説明文に使わない（厚木市・県観光協会の解説ページはどちらも「厚木のホルモン」と書き、「シロコロ」を使っていない）
 * - サンマーメン：名前の由来を一つに決めない（農水省が「諸説ある」と書いているため）
 * - 横浜家系ラーメン：発祥の店の名前は書かない（県観光協会の名産100選に「〜が発祥といわれ」とあるが、店名になり、自治体資料で確かめられない）
 * - しらす丼：生しらすが出ない日がある、といった店の事情は書かない（出典は「水揚げ当日にしか食べられない」まで）
 * - よこすか海軍カレー：提供店の数（40店以上・令和3年）は書かない（すぐ変わる情報）
 * - よこすか海軍カレー：「日本のカレーのルーツ」は言い切らない（農水省・県観光協会とも「一説」「〜とも言われています」の書き方）
 * - 三崎まぐろ丼：水揚げ量の順位などの数字は書かない（出典にない）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { kanagawaSceneryTour } from './kanagawaScenes';
import { KANAGAWA_FOOD_HULLS } from './kanagawaFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'odawara-kamaboko',
    level: 1,
    name: '小田原かまぼこ',
    shortName: '小田原かまぼこ',
    image: '/assets/foods/kanagawa/01_odawara_kamaboko.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '白身魚のグチを身落とし、水さらし、脱水を経て石臼で塩を加えて練り、裏ごしして、板に盛り付けて約90度の蒸気で蒸し焼きにする。板からぷっくりとはみ出した扇形が特徴。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_19_kanagawa.html',
    icon: { label: '小田原かまぼこ', shape: 'mochi', baseColor: '#f6f4ee', accentColor: '#d9c9a0' },
    reward: {
      unlockScore: 200,
      area: '小田原市',
      headline: '板からはみ出した扇形',
      tip: '平安時代に描かれた祝宴の図に初めて「蒲鉾」の文字が登場する。当初は竹を芯にして焼いたちくわのようなもので、蒲の穂や鉾に似ていることからこの名になった。',
      links: searchLinks('小田原市', '小田原かまぼこ'),
    },
  },
  {
    id: 'shumai',
    level: 2,
    name: 'シューマイ',
    shortName: 'シューマイ',
    image: '/assets/foods/kanagawa/02_shumai.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '豚ひき肉を塩漬けにして練り、皮で包み、グリーンピースを上に乗せて約7分強火で蒸す。崎陽軒は南京街（いまの横浜中華街）で突き出しとして出されていたシューマイにヒントを得て、具に干し帆立貝柱を使い、冷めてもおいしいものを開発した。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_22_kanagawa.html',
    icon: { label: 'シューマイ', shape: 'dumpling', baseColor: '#e8dcc0', accentColor: '#6b9a3a' },
    reward: {
      unlockScore: 500,
      area: '横浜市',
      headline: 'てっぺんに緑がひとつ',
      tip: '横浜市はシューマイの消費額日本一の都市。全国におけるシューマイ（冷凍食品や飲食店での食事は除く）の年間平均消費額が1,093円であるのに対し、横浜市は2,248円と2倍以上になる。',
      links: searchLinks('横浜市', 'シューマイ'),
    },
  },
  {
    id: 'odawara-oden',
    level: 3,
    name: '小田原おでん',
    shortName: '小田原おでん',
    image: '/assets/foods/kanagawa/03_odawara_oden.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '城下町・宿場町として栄えた小田原で育った練り物文化から生まれたおでん。地元の老舗かまぼこ店などがつくったおでん種を味わい、仕上げに曽我の梅林の梅で作った梅みそを付けて食べる。',
    source:
      'https://www.city.odawara.kanagawa.jp/kanko/event/OCT/odennmaturi.html',
    icon: { label: 'おでん', shape: 'pot', baseColor: '#e8d3a0', accentColor: '#d65a6a' },
    reward: {
      unlockScore: 1000,
      area: '小田原市',
      headline: 'かまぼこの町のおでん',
      tip: '「かながわの名産100選」に選ばれている。秋には小田原城址公園の二の丸広場で、市内の店のおでん種を味わう「小田原おでん祭り」が開かれる。',
      links: searchLinks('小田原市', '小田原おでん'),
      official: [{ label: '神奈川県観光協会「かながわの名産100選」', url: 'https://www.kanagawa-kankou.or.jp/features/goods100' }],
    },
  },
  {
    id: 'atsugi-shirokoro',
    level: 4,
    name: '厚木シロコロ・ホルモン',
    shortName: '厚木のホルモン',
    image: '/assets/foods/kanagawa/04_atsugi_shirokoro.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '厚木でホルモンといえば豚ホルモンのこと。脂身を残したやわらかい生の豚の大腸を炭火で焼く。やわらかな歯ごたえと、口いっぱいに広がる脂身が特徴。',
    source:
      'https://www.kanagawa-kankou.or.jp/features/gourmet',
    icon: { label: 'ホルモン', shape: 'dumpling', baseColor: '#f1e3d0', accentColor: '#b5733a' },
    reward: {
      unlockScore: 1800,
      area: '厚木市',
      headline: '脂を残した大腸を炭火で',
      tip: '市内に食肉センターがあり、新鮮な豚肉が流通している。市内には昔から多くのホルモン焼き店があり、店ごとの自家製のみそだれで豚の内臓のいろいろな部位が味わえる。',
      links: searchLinks('厚木市', 'シロコロホルモン'),
      official: [{ label: '厚木市「厚木のホルモン」（あつぎ食ブランド）', url: 'https://www.city.atsugi.kanagawa.jp/kanko_reja/city/2/22745.html' }],
    },
  },
  {
    id: 'sanma-men',
    level: 5,
    name: 'サンマーメン',
    shortName: 'サンマーメン',
    image: '/assets/foods/kanagawa/05_sanma_men.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '豚肉ともやし、白菜などを炒めてスープを入れ、とろみをつけた野菜あんを、ラーメンの上にかける。名前の由来には諸説あり、「生馬麺」と書くという説がある。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_16_kanagawa.html',
    icon: { label: 'サンマー麺', shape: 'ramen', baseColor: '#e8c27a', accentColor: '#7aa04a' },
    reward: {
      unlockScore: 3000,
      area: '横浜市ほか県内全域',
      headline: 'とろみの野菜あんをのせた麺',
      tip: '戦前の横浜中華街で、高価だった肉そばの代わりに、まかないとして作られたのが始まり。あんをかけるとスープが冷めにくく、県内各地の中華料理店に広まった。',
      links: searchLinks('横浜市', 'サンマーメン'),
      official: [{ label: '神奈川県観光協会「一度は食べたい！神奈川名物グルメ＆お土産特集」', url: 'https://www.kanagawa-kankou.or.jp/features/gourmet' }],
    },
  },
  {
    id: 'oyama-tofu',
    level: 6,
    name: '大山の豆腐',
    shortName: '大山の豆腐',
    image: '/assets/foods/kanagawa/06_oyama_tofu.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '大山は標高1,252メートルの霊山で、参拝の人びとをもてなす料理として豆腐が使われてきた。冷奴、湯豆腐、白和え、豆腐の田楽などの形で食べられている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_2_kanagawa.html',
    icon: { label: '大山の豆腐', shape: 'rice-bowl', baseColor: '#f6f4ec', accentColor: '#3f6b2a' },
    reward: {
      unlockScore: 4500,
      area: '伊勢原市',
      headline: '参拝の人をもてなす豆腐',
      tip: '江戸の人口が100万人の頃、ひと夏に20万人もの参拝客が大山に足を運んだという。',
      links: searchLinks('伊勢原市', '大山豆腐'),
    },
  },
  {
    id: 'iekei-ramen',
    level: 7,
    name: '横浜家系ラーメン',
    shortName: '家系ラーメン',
    image: '/assets/foods/kanagawa/07_iekei_ramen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '豚骨醤油をベースにした濃厚なスープと、小麦の味がしっかり感じられる中太麺が特徴のラーメン。本場は横浜で、コクのある深い味わいが楽しめる。',
    source:
      'https://www.kanagawa-kankou.or.jp/features/yokohama-kawasaki',
    icon: { label: '家系', shape: 'ramen', baseColor: '#d9b07a', accentColor: '#1f3a1f' },
    reward: {
      unlockScore: 6500,
      area: '横浜市',
      headline: '豚骨醤油の濃厚スープ',
      tip: '「かながわの名産100選」に選ばれている。名産100選の紹介では、豚骨醤油ベースの濃厚スープと、太いストレート麺が特徴とされる。',
      links: searchLinks('横浜市', '家系ラーメン'),
    },
  },
  {
    id: 'shirasu-don',
    level: 8,
    name: 'しらす丼',
    shortName: 'しらす丼',
    image: '/assets/foods/kanagawa/08_shirasu_don.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '相模湾でとれたしらすをご飯にのせた丼。ゆでたものを「釜揚げしらす」、加工していない生のものを「生しらす」と呼ぶ。生しらすは鮮度が落ちやすく、水揚げ当日にしか食べられない。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_3_kanagawa.html',
    icon: { label: 'しらす丼', shape: 'rice-bowl', baseColor: '#f4f1e8', accentColor: '#b8c4c8' },
    reward: {
      unlockScore: 9000,
      area: '鎌倉市ほか湘南地域',
      headline: '釜揚げと生、二色のしらす',
      tip: '漁期は3月11日から12月31日まで。春と秋が最盛期で、1月1日から3月10日は禁漁になる。茅ヶ崎・鎌倉・江の島など湘南の名物として知られる。',
      links: searchLinks('鎌倉市', 'しらす丼'),
      official: [{ label: '神奈川県観光協会「かながわの名産100選」（湘南しらす丼）', url: 'https://www.kanagawa-kankou.or.jp/features/goods100' }],
    },
  },
  {
    id: 'yokosuka-navy-curry',
    level: 9,
    name: 'よこすか海軍カレー',
    shortName: '海軍カレー',
    image: '/assets/foods/kanagawa/09_yokosuka_navy_curry.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '肉、にんじん、玉ねぎ、じゃがいもをさいの目に切り、小麦粉とカレー粉で煮込んでとろみをつけ、ご飯にかける。日本のカレーのルーツの一説とされる明治の海軍の食事を、横須賀市が平成11年に再現した。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_5_kanagawa.html',
    icon: { label: '海軍カレー', shape: 'curry-plate', baseColor: '#8a5a2b', accentColor: '#f4f1e8' },
    reward: {
      unlockScore: 12000,
      area: '横須賀市',
      headline: '海軍の献立をいまに再現',
      tip: '地元の認定店では、栄養のバランスを考えていた海軍の献立にならい、サラダと牛乳をセットにして出すことがルールとされている。',
      links: searchLinks('横須賀市', 'よこすか海軍カレー'),
      official: [{ label: '神奈川県観光協会「一度は食べたい！神奈川名物グルメ＆お土産特集」', url: 'https://www.kanagawa-kankou.or.jp/features/gourmet' }],
    },
  },
  {
    id: 'ton-zuke',
    level: 10,
    name: 'とん漬け',
    shortName: 'とん漬け',
    image: '/assets/foods/kanagawa/10_ton_zuke.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'みりんや醤油、にんにくなどを調合した特製の味噌に豚肉を漬け込み、焦がさないように焼く。もとは江戸末期、荻野山中藩で、何の肉か分からないようイノシシの肉に味噌を塗って焼いて出したところ大変好評だったことが始まり。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_6_kanagawa.html',
    icon: { label: 'とん漬け', shape: 'curry-plate', baseColor: '#7a4020', accentColor: '#c98a4a' },
    reward: {
      unlockScore: 16000,
      area: '厚木市',
      headline: '何の肉か分からないように',
      tip: '明治の文明開化とともに肉食が広がり、港をもつ神奈川県では養豚業がさかんになった。厚木には一時300軒もの養豚場があり、やがて厚木の名産になった。',
      links: searchLinks('厚木市', 'とん漬け'),
    },
  },
  {
    id: 'misaki-maguro-don',
    level: 11,
    name: '三崎まぐろ丼',
    shortName: '三崎まぐろ丼',
    image: '/assets/foods/kanagawa/11_misaki_maguro_don.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '日本有数のまぐろの水揚げ基地とされる三崎港の、定番のまぐろ料理。三崎では、まぐろ丼のほか、定食や、まぐろの頭からとった出汁を使うラーメンなど、さまざまなまぐろ料理が出される。',
    source:
      'https://www.kanagawa-kankou.or.jp/features/miura',
    icon: { label: 'まぐろ丼', shape: 'rice-bowl', baseColor: '#b0242c', accentColor: '#f4f1e8' },
    reward: {
      unlockScore: 22000,
      area: '三浦市三崎',
      headline: 'まぐろの港の定番丼',
      tip: '三崎のまぐろは船の上でマイナス60度に急速冷凍され、鮮度が保たれる。「かながわの名産100選」に選ばれている。',
      links: searchLinks('三浦市三崎', 'まぐろ丼'),
    },
  },
  {
    id: 'gyunabe',
    level: 12,
    name: '牛鍋',
    shortName: '牛鍋',
    image: '/assets/foods/kanagawa/12_gyunabe.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '味噌や醤油を使ったたれで牛肉を煮込む、横浜市発祥とされる鍋料理。牛もも肉、長ねぎ、春菊、しいたけ、焼き豆腐を、醤油・みりん・砂糖・赤みそのたれで煮る。煮えた具材を溶き卵につけて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/35_17_kanagawa.html',
    icon: { label: '牛鍋', shape: 'pot', baseColor: '#8a2f28', accentColor: '#e8c84a' },
    reward: {
      unlockScore: 30000,
      area: '横浜市',
      headline: '1862年、初の牛鍋屋とされる店が開業',
      tip: '1859年に開港した横浜では、西洋文化の影響を受けて国内でもいち早く肉食文化が広まった。居酒屋「伊勢熊」の主人が1862年に初の牛鍋屋を開業したが、当時は牛肉を食べることを嫌う人も多く、妻の反対があったため店を二分していた。',
      links: searchLinks('横浜市', '牛鍋'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const kanagawaFoodStage: StageDefinition = {
  regionId: 'kanagawa',
  regionName: '神奈川県',
  mode: 'food',
  title: '神奈川めしマージ',
  theme: {
    primaryColor: '#1f4e79',
    secondaryColor: '#ece5d8',
    accentColor: '#b5472e',
    backgroundColor: '#f8f4ec',
  },
  /** 背景ツアー「神奈川めぐり」。軸は季節。4月の三溪園から始めて、3月の猿島で終わる。箱根と三浦半島を行き来する。 */
  scenery: {
    tour: kanagawaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, KANAGAWA_FOOD_HULLS),
};
